import React, { useEffect, useState } from 'react';
import {
    GridCellParams, GridColumnOrderChangeParams, GridColumnResizeParams,
    GridRowId,
    GridSelectionModel,
} from "@mui/x-data-grid-pro";
import {
    CartList,
    useGetCartQuery,
} from "../../services/grace";
import CheckoutDialog from "../../components/dialogs/CartCheckout/CartCheckout";
import CartRemoveDialog from "../../components/dialogs/CartRemoveDialog/CartRemoveDialog";
import CartGrid from "../../components/grids/CartGrid/CartGrid";
import cartIcon from "../../assets/icons/van.png";
import GDGAppBar from "../../components/app/GDGAppBar/GDGAppBar";
import GDGAppBarButton from "../../components/app/GDGAppBar/GDGAppBarButton";
import { Box} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../util/hooks";
import { RootState } from "../../util/store";
import { setColumnDef, setSelectionModel } from "../../features/cart/cartSlice";
import { GridColumnDef } from "../../components/grids/grid";
import OrderReceiptDialog from "../../components/dialogs/CartRemoveDialog/OrderReceiptDialog";
import { useAlertDispatch } from '../../context/AlertContext/AlertContext';

enum PageModes {
    List = 'list',
    Edit = 'Edit',
    ConfirmRemove = 'ConfirmRemove',
    Checkout = 'Checkout',
    Receipt = 'Receipt',
}

declare type PageMode = PageModes.List | PageModes.Edit | PageModes.ConfirmRemove | PageModes.Checkout | PageModes.Receipt;

export default function Cart(): JSX.Element {
    const dispatch = useAppDispatch();
    const alertDispatch = useAlertDispatch();
    const [pageMode, setPageMode] = useState<PageMode>(PageModes.List);

    const { data, refetch, isError, isFetching } = useGetCartQuery(null);
    const [cartRows, setCartRows] = useState<CartList>([]);
    const [removeItems, setRemoveItems] = useState<Array<string>>([]);
    const selectionModel = useAppSelector<Array<GridRowId>>((state) => state.cart.selectionModel);
    const [receiptId, setReceiptId] = useState("");
    const columnDef = useAppSelector((state: RootState) => state.cart.columnDef);

    useEffect(() => {
        // No error handling required as it's handled in AppDrawer.tsx
        if (isFetching) {
            setCartRows([]);
        } else if (!isError && data !== undefined) {
            setCartRows(data);
        }

    }, [data, isFetching, isError]);

    const handleSelectionModelChange = (model: GridSelectionModel) => {
        dispatch(setSelectionModel(model));
    };

    const handleCheckoutClose = () => {
        setPageMode(PageModes.List);
    };

    const handleCheckoutSubmit = (id: string) => {
        setReceiptId(id);
        setPageMode(PageModes.Receipt);
    };

    const handleReceiptClose = () => {
        setPageMode(PageModes.List);
    };

    const handleCartRemoveClose = () => {
        setRemoveItems([]);
        setPageMode(PageModes.List);
    };

    const handleCartRemoveSubmit = () => {
        dispatch(setSelectionModel([]));
        setRemoveItems([]);
        setPageMode(PageModes.List);
    };

    const handleCellClick = (params: GridCellParams, event: React.MouseEvent) => {
        if (params.colDef.field === 'remove') {
            event.stopPropagation();
            if (params.row.deletable) {
                setRemoveItems([params.id.toString()]);
                setPageMode(PageModes.ConfirmRemove);
            }
        }
    };

    const handleCartCheckout = () => {
        if (cartRows.length > 0 && selectionModel.length > 0) {
            setPageMode(PageModes.Checkout);
        } else {
            alertDispatch({
                type: 'add',
                payload: {
                    type: "general",
                    message: "No items selected for checkout",
                }
            });
        }
    };

    const handleCartRemove = () => {
        if (selectionModel.length > 0) {
            const items: Array<string> = [];
            selectionModel.forEach(item => items.push(item.toString()));
            setRemoveItems(items);
            setPageMode(PageModes.ConfirmRemove);
        }
    };
    const handleCartReload = () => {
        refetch();
    };

    const handleColumnResize = (params: GridColumnResizeParams) => {
        const newColumnDef:GridColumnDef[] = [];
        columnDef.forEach((item) => {
            if (item.field === params.colDef.field) {
                newColumnDef.push({...item, width: params.width});
            } else {
                newColumnDef.push({...item});
            }
        });
        dispatch(setColumnDef(newColumnDef));
    };

    // Indexes are minus one due to the column definition not
    // containing the automatically added checkbox column
    const handleColumnReorder = (params: GridColumnOrderChangeParams) => {
        const newColumnDef = [...columnDef];
        const item = newColumnDef.splice(params.oldIndex - 1, 1);
        newColumnDef.splice(params.targetIndex - 1, 0, item[0])
        dispatch(setColumnDef(newColumnDef));
    };

    return (
        <Box sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <GDGAppBar title={'Cart'}>
                <GDGAppBarButton
                    onClick={handleCartReload}
                    text='Reload'
                />
                <GDGAppBarButton
                    onClick={handleCartRemove}
                    text='Remove'
                />
                <GDGAppBarButton
                    onClick={handleCartCheckout}
                    text='Check Out'
                />
            </GDGAppBar>

            <CheckoutDialog
                open={pageMode === PageModes.Checkout}
                count={selectionModel.length}
                onClose={handleCheckoutClose}
                onSubmit={handleCheckoutSubmit}
            />

            <OrderReceiptDialog
                open={pageMode === PageModes.Receipt}
                receiptId={receiptId}
                onClose={handleReceiptClose}
                onSubmit={handleCheckoutClose}
                maxWidth="lg"
            />

            <CartRemoveDialog
                open={pageMode === PageModes.ConfirmRemove}
                text={`Do you want to remove ${removeItems.length > 1 ? 'these items' : 'this item'} from the cart?`}
                removeItems={removeItems}
                onClose={handleCartRemoveClose}
                onSubmit={handleCartRemoveSubmit}
            />

            <CartGrid
                rows={cartRows}
                columnDef={columnDef}
                loading={isFetching}
                selectionModel={selectionModel}
                onSelectionModelChange={handleSelectionModelChange}
                onCellClick={handleCellClick}
                onColumnResize={handleColumnResize}
                onColumnOrderChange={handleColumnReorder}
            />
        </Box>
    );
}
